import React, { useState, useRef } from 'react';
import './MultiImageUploader.css';

const MultiImageUploader = ({ onUploadComplete, category = 'dishes', maxFiles = 10 }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const validFiles = selectedFiles.filter(file => {
      const isValidType = file.type.startsWith('image/');
      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
      return isValidType && isValidSize;
    });

    if (validFiles.length !== selectedFiles.length) {
      alert('Some files were skipped. Only image files under 5MB are allowed.');
    }

    setFiles(prev => [...prev, ...validFiles].slice(0, maxFiles));
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadToCloudflareImages = async (file, index) => {
    try {
      // Get direct upload URL with proper metadata for folder organization
      const response = await fetch('/api/images/direct-upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('admin_token') || 'Boodgy'}`
        },
        body: JSON.stringify({
          metadata: {
            type: category, // 'dishes', 'chefs', 'brand', etc.
            category: category === 'dishes' ? 'gourmet-dishes' : category,
            folder: category === 'dishes' ? 'dishes' : 
                   category === 'chefs' ? 'chefs' : 
                   category === 'brand' ? 'brand' : 'misc',
            fileName: file.name,
            uploadedAt: new Date().toISOString(),
            fileSize: file.size,
            fileType: file.type,
            uploadedBy: 'admin-dashboard',
            purpose: category === 'dishes' ? 'gourmet-dish-photo' : 'profile-photo'
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadURL } = await response.json();

      // Upload file to Cloudflare Images
      const formData = new FormData();
      formData.append('file', file);

      const uploadResponse = await fetch(uploadURL, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      const uploadResult = await uploadResponse.json();
      console.log('Cloudflare Images upload result:', uploadResult);
      
      // Cloudflare Images returns { id: "image-id", filename: "filename.jpg" }
      // We need to construct the delivery URL
      if (uploadResult.id) {
        return {
          id: uploadResult.id,
          filename: uploadResult.filename || file.name,
          url: `https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/${uploadResult.id}/public`
        };
      } else {
        throw new Error('Upload response missing image ID');
      }
    } catch (error) {
      console.error('Upload error:', error);
      throw error;
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;

    setUploading(true);
    setUploadProgress({});
    const results = [];

    try {
      for (let i = 0; i < files.length; i++) {
        setUploadProgress(prev => ({ ...prev, [i]: 'uploading' }));
        
        try {
          const result = await uploadToCloudflareImages(files[i], i);
          results.push({
            file: files[i],
            result: result,
            index: i
          });
          setUploadProgress(prev => ({ ...prev, [i]: 'completed' }));
        } catch (error) {
          setUploadProgress(prev => ({ ...prev, [i]: 'error' }));
          console.error(`Upload failed for file ${i}:`, error);
        }
      }

      setUploadedImages(results);
      if (onUploadComplete) {
        onUploadComplete(results);
      }
    } finally {
      setUploading(false);
    }
  };

  const resetUploader = () => {
    setFiles([]);
    setUploadProgress({});
    setUploadedImages([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="multi-image-uploader">
      <div className="uploader-header">
        <h3>Multi-Image Uploader</h3>
        <p>Upload up to {maxFiles} gourmet dish images</p>
      </div>

      {/* File Selection */}
      <div className="file-selection">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
          className="file-input"
        />
        <button 
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="select-files-btn"
        >
          Select Images
        </button>
      </div>

      {/* File Preview */}
      {files.length > 0 && (
        <div className="file-preview">
          <h4>Selected Files ({files.length}/{maxFiles})</h4>
          <div className="file-grid">
            {files.map((file, index) => (
              <div key={index} className="file-item">
                <div className="file-preview-img">
                  <img 
                    src={URL.createObjectURL(file)} 
                    alt={file.name}
                    className="preview-image"
                  />
                  <div className="file-info">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>
                <div className="file-status">
                  {uploadProgress[index] === 'uploading' && (
                    <span className="status uploading">⏳ Uploading...</span>
                  )}
                  {uploadProgress[index] === 'completed' && (
                    <span className="status completed">✅ Complete</span>
                  )}
                  {uploadProgress[index] === 'error' && (
                    <span className="status error">❌ Error</span>
                  )}
                </div>
                <button 
                  onClick={() => removeFile(index)}
                  disabled={uploading}
                  className="remove-btn"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Upload Controls */}
      {files.length > 0 && (
        <div className="upload-controls">
          <button 
            onClick={handleUpload}
            disabled={uploading}
            className="upload-btn"
          >
            {uploading ? 'Uploading...' : `Upload ${files.length} Images`}
          </button>
          <button 
            onClick={resetUploader}
            disabled={uploading}
            className="reset-btn"
          >
            Reset
          </button>
        </div>
      )}

      {/* Upload Results */}
      {uploadedImages.length > 0 && (
        <div className="upload-results">
          <h4>Upload Results</h4>
          <div className="results-grid">
            {uploadedImages.map((item, index) => (
              <div key={index} className="result-item">
                <img 
                  src={`https://imagedelivery.net/YQhisQdXT8651Bk9Z7c9KQ/${item.result.id}/public`}
                  alt={item.file.name}
                  className="result-image"
                />
                <div className="result-info">
                  <span className="result-name">{item.file.name}</span>
                  <span className="result-id">ID: {item.result.id}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiImageUploader;
